import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiSend, FiMail, FiEye, FiUsers, FiAlertTriangle, FiChevronDown,
  FiCheckCircle, FiXCircle, FiBarChart2, FiZap, FiX, FiClock,
} from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

// ---------------------------------------------------------------------------
// Keep these segment rules in sync with
// supabase/functions/send-reengagement-campaign/index.ts
// ---------------------------------------------------------------------------
const DORMANT_DAYS = 30;

interface ProfileLite {
  email: string | null;
  first_name: string | null;
  name: string | null;
  is_in_trial: boolean | null;
  subscription_status: string | null;
  billing_type: string | null;
  last_login: string | null;
}

const isTrial = (p: ProfileLite) =>
  p.is_in_trial === true || ['trial', 'trialing'].includes((p.subscription_status || '').toLowerCase());
const isLifetime = (p: ProfileLite) => (p.billing_type || '').toLowerCase() === 'lifetime';
const isActive = (p: ProfileLite) =>
  (p.subscription_status || '').toLowerCase() === 'active' && !isTrial(p) && !isLifetime(p);
const isDormant = (p: ProfileLite) => {
  if (!p.last_login) return true;
  return new Date(p.last_login).getTime() < Date.now() - DORMANT_DAYS * 86400000;
};

type Segment = 'all' | 'active' | 'trial' | 'dormant' | 'lifetime';

const SEGMENTS: { id: Segment; label: string; help: string }[] = [
  { id: 'all', label: 'All users', help: 'Everyone with an email' },
  { id: 'active', label: 'Active (paying)', help: 'Active paid subscription' },
  { id: 'trial', label: 'In trial', help: 'Currently in a trial' },
  { id: 'dormant', label: 'Dormant', help: `No login in ${DORMANT_DAYS}+ days` },
  { id: 'lifetime', label: 'Lifetime', help: 'Lifetime access' },
];

const inSegment = (p: ProfileLite, s: Segment) =>
  s === 'all' ? true :
  s === 'active' ? isActive(p) :
  s === 'trial' ? isTrial(p) :
  s === 'dormant' ? isDormant(p) :
  s === 'lifetime' ? isLifetime(p) : false;

interface Recipient {
  id: string;
  email: string;
  name: string | null;
  status: string;
  error_message: string | null;
  sent_at: string | null;
}
interface Campaign {
  id: string;
  sent_at: string;
  segment: string;
  subject: string;
  trial_days: number;
  emails_sent: number;
  fail_count: number;
  skipped_count: number;
  mode?: string;
  status?: string;
  recipients?: Recipient[];
  recipientsLoading?: boolean;
}

const PREVIEW_SAMPLE = { first_name: 'Alex' };

const ReengagementCampaignPanel: React.FC = () => {
  const [profiles, setProfiles] = useState<ProfileLite[]>([]);
  const [optedOut, setOptedOut] = useState<Set<string>>(new Set());
  const [loadingCounts, setLoadingCounts] = useState(true);

  const [segment, setSegment] = useState<Segment>('all');
  const [trialDays, setTrialDays] = useState(7);
  const [sendMode, setSendMode] = useState<'instant' | 'drip'>('drip');
  const [testEmail, setTestEmail] = useState('');

  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [showConfirm, setShowConfirm] = useState(false);

  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  const [activeTab, setActiveTab] = useState<'compose' | 'history'>('compose');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    fetchAudience();
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.email) setTestEmail(data.user.email);
    });
  }, []);

  useEffect(() => {
    if (activeTab === 'history') fetchCampaigns();
  }, [activeTab]);

  const fetchAudience = async () => {
    setLoadingCounts(true);
    try {
      const [{ data: profs }, { data: outs }] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('email, first_name, name, is_in_trial, subscription_status, billing_type, last_login')
          .not('email', 'is', null),
        supabase.from('email_unsubscribes').select('email'),
      ]);
      setProfiles((profs || []) as ProfileLite[]);
      setOptedOut(new Set((outs || []).map((o: any) => (o.email || '').toLowerCase())));
    } catch (err) {
      console.error('Error loading audience:', err);
    } finally {
      setLoadingCounts(false);
    }
  };

  // De-duplicated, opt-out-excluded counts per segment.
  const counts = useMemo(() => {
    const result: Record<Segment, number> = { all: 0, active: 0, trial: 0, dormant: 0, lifetime: 0 };
    for (const s of SEGMENTS) {
      const seen = new Set<string>();
      for (const p of profiles) {
        const e = (p.email || '').toLowerCase();
        if (!e || seen.has(e) || optedOut.has(e)) continue;
        if (inSegment(p, s.id)) { seen.add(e); result[s.id]++; }
      }
    }
    return result;
  }, [profiles, optedOut]);

  const targetCount = counts[segment];

  const fetchCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      const { data, error } = await supabase
        .from('reengagement_campaigns')
        .select('*')
        .order('sent_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      setCampaigns((data || []) as Campaign[]);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    } finally {
      setCampaignsLoading(false);
    }
  };

  const toggleCampaign = async (id: string) => {
    if (expanded === id) { setExpanded(null); return; }
    setCampaigns(prev => prev.map(c => c.id === id ? { ...c, recipientsLoading: true } : c));
    try {
      const { data } = await supabase
        .from('reengagement_recipients')
        .select('*')
        .eq('campaign_id', id)
        .order('status', { ascending: false });
      setCampaigns(prev => prev.map(c =>
        c.id === id ? { ...c, recipients: (data || []) as Recipient[], recipientsLoading: false } : c));
      setExpanded(id);
    } catch (err) {
      console.error('Error fetching recipients:', err);
      setCampaigns(prev => prev.map(c => c.id === id ? { ...c, recipientsLoading: false } : c));
    }
  };

  const buildPreview = async () => {
    const appUrl = 'https://snap-chart.ai';
    const vars: Record<string, string> = {
      first_name: PREVIEW_SAMPLE.first_name,
      login_url: `${appUrl}/login`,
      redeem_url: '#',
      trial_days: String(trialDays),
      unsubscribe_url: '#',
      app_url: appUrl,
    };
    let inner = '';
    try {
      const { data } = await supabase
        .from('email_templates')
        .select('html_content')
        .eq('template_key', 'reengagement')
        .maybeSingle();
      inner = data?.html_content || '';
    } catch { /* fall through */ }

    if (!inner) {
      setPreviewHtml('<p style="padding:24px;color:#b91c1c;">No "reengagement" template found in email_templates. The Edge Function will use its built-in fallback copy.</p>');
      setShowPreview(true);
      return;
    }
    for (const [k, v] of Object.entries(vars)) {
      inner = inner.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}`, 'g'), v);
    }
    // Approximate the shared wrapEmailHtml header/footer for a faithful preview.
    const wrapped = `
      <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;background:#fafafa;padding:48px 24px;">
        <div style="max-width:580px;margin:0 auto;">
          <div style="margin-bottom:40px;"><span style="font-size:20px;font-weight:600;color:#0f172a;">Snap Chart AI</span></div>
          <div style="background:#fff;border:1px solid #e5e7eb;border-radius:16px;padding:40px;">${inner}</div>
          <div style="margin-top:40px;padding-top:24px;border-top:1px solid #e5e7eb;color:#9ca3af;font-size:13px;line-height:1.6;">
            SnapChart AI &middot; snap-chart.ai &middot; support@snap-chart.ai
          </div>
        </div>
      </div>`;
    setPreviewHtml(wrapped);
    setShowPreview(true);
  };

  const callFunction = async (payload: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke('send-reengagement-campaign', { body: payload });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const handleTestSend = async () => {
    if (!testEmail) return;
    setSending(true);
    setResult(null);
    try {
      await callFunction({ testEmail, trialDays });
      setResult({ ok: true, message: `Test email sent to ${testEmail}. Check the inbox (and spam) before sending the real campaign.` });
    } catch (err: any) {
      setResult({ ok: false, message: err.message || 'Test send failed' });
    } finally {
      setSending(false);
    }
  };

  const handleSendCampaign = async () => {
    setShowConfirm(false);
    setSending(true);
    setResult(null);
    try {
      const data = await callFunction({ segment, trialDays, mode: sendMode, ratePerHour: 20 });
      setResult({
        ok: true,
        message: sendMode === 'drip'
          ? `Queued ${data.queued} user${data.queued === 1 ? '' : 's'} — sending ~${data.ratePerHour}/hour automatically until done${data.skippedCount ? `, ${data.skippedCount} skipped (unsubscribed)` : ''}. Track progress in History.`
          : `Campaign sent — ${data.emailsSent} delivered, ${data.failCount} failed, ${data.skippedCount} skipped (unsubscribed).`,
      });
      await fetchCampaigns();
      setActiveTab('history');
    } catch (err: any) {
      setResult({ ok: false, message: err.message || 'Campaign failed' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <FiZap className="text-cyan-400" />
          Re-engagement Campaign
        </h2>
        <p className="text-gray-400 text-sm mt-1">
          Send a personalized win-back email with a reactivated trial offer. Test on your own inbox first.
        </p>
      </div>

      {/* Result banner */}
      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className={`p-3 rounded-lg text-sm flex items-start gap-2 ${result.ok ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-red-500/10 text-red-400 border border-red-500/20'}`}
          >
            {result.ok ? <FiCheckCircle className="mt-0.5 shrink-0" /> : <FiXCircle className="mt-0.5 shrink-0" />}
            <span>{result.message}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10">
        {(['compose', 'history'] as const).map(t => (
          <button
            key={t}
            onClick={() => setActiveTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === t ? 'border-cyan-500 text-white' : 'border-transparent text-gray-400 hover:text-white'}`}
          >
            {t === 'compose' ? <><FiMail className="inline mr-2" />Compose</> : <><FiBarChart2 className="inline mr-2" />History</>}
          </button>
        ))}
      </div>

      {activeTab === 'compose' && (
        <div className="space-y-6">
          {/* Segment selector */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Target segment</label>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {SEGMENTS.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSegment(s.id)}
                  className={`p-3 rounded-xl border text-left transition-colors ${segment === s.id ? 'bg-cyan-500/15 border-cyan-500/40' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
                >
                  <div className="text-sm font-semibold text-white">{s.label}</div>
                  <div className="text-xs text-gray-400 mt-0.5">{s.help}</div>
                  <div className="text-lg font-bold text-cyan-400 mt-1">
                    {loadingCounts ? '…' : counts[s.id]}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Trial days */}
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Reactivated trial length</label>
              <div className="flex items-center gap-2">
                <input
                  type="number" min={1} max={90} value={trialDays}
                  onChange={e => setTrialDays(Math.max(1, Math.min(90, Number(e.target.value) || 1)))}
                  className="w-24 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                />
                <span className="text-gray-400 text-sm">days</span>
              </div>
            </div>
            <button
              onClick={buildPreview}
              className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm text-gray-200 flex items-center gap-2"
            >
              <FiEye /> Preview email
            </button>
          </div>

          {/* Test send */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <FiAlertTriangle className="text-yellow-400" />
              <span className="text-sm font-semibold text-white">Step 1 — Test on your own inbox</span>
            </div>
            <p className="text-xs text-gray-400 mb-3">Always send a test to yourself before blasting the segment. Verify rendering, links, and that it lands in the inbox (not spam).</p>
            <div className="flex flex-wrap gap-2">
              <input
                type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
                placeholder="you@example.com"
                className="flex-1 min-w-[220px] px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
              />
              <button
                onClick={handleTestSend}
                disabled={sending || !testEmail}
                className="px-4 py-2 bg-yellow-600/80 hover:bg-yellow-600 disabled:opacity-40 rounded-lg text-sm text-white font-medium flex items-center gap-2"
              >
                {sending ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiSend />}
                Send test
              </button>
            </div>
          </div>

          {/* Send campaign */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <FiUsers className="text-cyan-400" />
              <span className="text-sm font-semibold text-white">Step 2 — Send to segment</span>
            </div>

            {/* Delivery mode */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              <button
                onClick={() => setSendMode('drip')}
                className={`p-3 rounded-lg border text-left transition-colors ${sendMode === 'drip' ? 'bg-cyan-500/15 border-cyan-500/40' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
              >
                <div className="text-sm font-semibold text-white flex items-center gap-2"><FiClock className="w-4 h-4" /> Drip (recommended)</div>
                <div className="text-xs text-gray-400 mt-0.5">~20/hour, auto-spreads under Resend's daily cap. Best deliverability.</div>
              </button>
              <button
                onClick={() => setSendMode('instant')}
                className={`p-3 rounded-lg border text-left transition-colors ${sendMode === 'instant' ? 'bg-cyan-500/15 border-cyan-500/40' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
              >
                <div className="text-sm font-semibold text-white flex items-center gap-2"><FiZap className="w-4 h-4" /> Send now</div>
                <div className="text-xs text-gray-400 mt-0.5">All at once. &gt;100/day may hit Resend's free limit.</div>
              </button>
            </div>

            <p className="text-xs text-gray-400 mb-3">
              {sendMode === 'drip' ? 'Will queue' : 'Will send to'} <strong className="text-cyan-400">{loadingCounts ? '…' : targetCount}</strong> user{targetCount === 1 ? '' : 's'} in
              {' '}<strong className="text-white">{SEGMENTS.find(s => s.id === segment)?.label}</strong>. Unsubscribed users are skipped automatically.
            </p>
            <button
              onClick={() => setShowConfirm(true)}
              disabled={sending || loadingCounts || targetCount === 0}
              className="px-5 py-2.5 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 rounded-lg text-sm text-white font-semibold flex items-center gap-2"
            >
              {sending ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiSend />}
              {sendMode === 'drip' ? 'Start drip campaign' : 'Send re-engagement campaign'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-3">
          {campaignsLoading ? (
            <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : campaigns.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <FiMail className="w-10 h-10 mx-auto mb-3 opacity-20" />
              <p className="font-medium">No campaigns yet</p>
            </div>
          ) : campaigns.map(c => {
            const isOpen = expanded === c.id;
            const recips = c.recipients || [];
            return (
              <div key={c.id} className="bg-[#0f1623] border border-white/8 rounded-xl overflow-hidden">
                <button onClick={() => toggleCampaign(c.id)} className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/4 text-left">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-9 h-9 rounded-lg bg-cyan-500/15 flex items-center justify-center text-cyan-400 shrink-0"><FiMail className="w-4 h-4" /></div>
                    <div className="min-w-0">
                      <p className="font-semibold text-white text-sm truncate">{c.subject}</p>
                      <p className="text-gray-500 text-xs mt-0.5 flex items-center gap-2">
                        <span className="px-1.5 py-0.5 bg-white/5 rounded">{c.segment}</span>
                        <FiClock className="inline" />
                        {new Date(c.sent_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-4">
                    {c.status === 'running' && <span className="px-2.5 py-1 bg-amber-500/15 text-amber-400 text-xs rounded-full font-semibold">⏳ drip running</span>}
                    <span className="px-2.5 py-1 bg-green-500/15 text-green-400 text-xs rounded-full font-semibold">{c.emails_sent} sent</span>
                    {c.fail_count > 0 && <span className="px-2.5 py-1 bg-red-500/15 text-red-400 text-xs rounded-full font-semibold">{c.fail_count} failed</span>}
                    {c.skipped_count > 0 && <span className="px-2.5 py-1 bg-gray-500/15 text-gray-400 text-xs rounded-full font-semibold">{c.skipped_count} skipped</span>}
                    {c.recipientsLoading ? <div className="w-4 h-4 border border-cyan-400 border-t-transparent rounded-full animate-spin ml-1" /> : <FiChevronDown className={`w-4 h-4 text-gray-500 transition-transform ml-1 ${isOpen ? 'rotate-180' : ''}`} />}
                  </div>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                      <div className="border-t border-white/8 max-h-72 overflow-y-auto">
                        {recips.length === 0 ? (
                          <p className="px-5 py-6 text-sm text-gray-500 text-center">No recipient detail.</p>
                        ) : recips.map(r => (
                          <div key={r.id} className="flex items-center justify-between px-5 py-2.5 border-b border-white/5 last:border-0">
                            <div className="flex items-center gap-3 min-w-0">
                              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${r.status === 'sent' ? 'bg-green-400' : r.status === 'failed' ? 'bg-red-400' : r.status === 'queued' ? 'bg-amber-400' : 'bg-gray-400'}`} />
                              <div className="min-w-0">
                                <p className="text-white text-sm truncate">{r.name || r.email}</p>
                                {r.name && <p className="text-gray-500 text-xs truncate">{r.email}</p>}
                              </div>
                            </div>
                            <span className={`px-2 py-0.5 text-xs rounded-full font-medium shrink-0 ml-4 ${r.status === 'sent' ? 'bg-green-500/15 text-green-400' : r.status === 'failed' ? 'bg-red-500/15 text-red-400' : r.status === 'queued' ? 'bg-amber-500/15 text-amber-400' : 'bg-gray-500/15 text-gray-400'}`}>
                              {r.status === 'sent' ? '✓ Delivered' : r.status === 'failed' ? '✗ Failed' : r.status === 'queued' ? '⏳ Pending' : 'Skipped'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <span className="text-sm font-semibold text-gray-700">Email preview (sample data)</span>
              <button onClick={() => setShowPreview(false)} className="text-gray-500 hover:text-gray-900"><FiX /></button>
            </div>
            <iframe title="email-preview" srcDoc={previewHtml} className="w-full flex-1 min-h-[60vh]" />
          </div>
        </div>
      )}

      {/* Confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowConfirm(false)}>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="bg-slate-900 border border-white/10 rounded-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/15 flex items-center justify-center text-cyan-400"><FiSend /></div>
              <h3 className="text-lg font-bold text-white">
                {sendMode === 'drip' ? `Queue ${targetCount} user${targetCount === 1 ? '' : 's'}?` : `Send to ${targetCount} user${targetCount === 1 ? '' : 's'}?`}
              </h3>
            </div>
            <p className="text-sm text-gray-400 mb-5">
              Segment <strong className="text-white">{SEGMENTS.find(s => s.id === segment)?.label}</strong> · {trialDays}-day trial offer.
              {sendMode === 'drip'
                ? ' Emails go out automatically at ~20/hour until everyone is reached (spreads over 2 days under the free Resend limit). Unsubscribed users are skipped.'
                : ' This sends real emails immediately and cannot be undone. Unsubscribed users are skipped.'}
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowConfirm(false)} className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm text-gray-300">Cancel</button>
              <button onClick={handleSendCampaign} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-sm text-white font-semibold flex items-center gap-2">
                <FiSend /> Confirm &amp; send
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ReengagementCampaignPanel;
